import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import type { BulkOrchestratorState } from '../hooks/useBulkEpisodeOrchestrator';

interface BulkCompletionSummaryProps {
  state: BulkOrchestratorState;
}

export function BulkCompletionSummary({ state }: BulkCompletionSummaryProps) {
  // Only count episodes where generate or save failed (fatal failures)
  // Localize/voices failures are non-fatal and count as succeeded
  const failedEpisodes = useMemo(
    () => state.episodes.filter((ep) => ep.steps.generate === 'fail' || ep.steps.save === 'fail'),
    [state.episodes]
  );

  const downloadReport = useCallback(() => {
    // CSV format per story spec: idea, status, episode_id, error, steps_completed
    const csvRows: string[] = ['"idea","status","episode_id","error","steps_completed"'];

    state.episodes.forEach((ep) => {
      const isFailed = ep.steps.generate === 'fail' || ep.steps.save === 'fail';
      const status = isFailed ? 'failed' : 'success';
      const stepsCompleted = Object.entries(ep.steps)
        .filter(([, s]) => s === 'ok')
        .map(([name]) => name)
        .join(';');
      const escapedIdea = ep.idea.replace(/"/g, '""');
      const escapedError = (ep.error || '').replace(/"/g, '""');
      csvRows.push(
        `"${escapedIdea}","${status}","${ep.episodeId || ''}","${escapedError}","${stepsCompleted}"`
      );
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-generation-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [state]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Generation Complete</h3>
        <div className="flex items-center gap-3">
          <Link
            to="/episodes"
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
          >
            View Generated Episodes
          </Link>
          <button
            type="button"
            onClick={downloadReport}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Download Report CSV
          </button>
        </div>
      </div>

      <div className="flex gap-8 text-sm">
        <div className="text-center">
          <div className="text-3xl font-bold text-green-600">{state.succeeded}</div>
          <div className="text-gray-500">Succeeded</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-red-600">{state.failed}</div>
          <div className="text-gray-500">Failed</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-gray-400">{state.episodes.length}</div>
          <div className="text-gray-500">Total</div>
        </div>
      </div>

      {failedEpisodes.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-2 text-sm font-medium text-red-700">Failed Episodes</h4>
          <div className="max-h-48 overflow-auto rounded border border-red-100 bg-red-50">
            {failedEpisodes.map((ep) => (
              <div
                key={ep.index}
                className="border-b border-red-100 px-3 py-2 text-xs last:border-0"
              >
                <span className="font-medium">#{ep.index + 1}</span>{' '}
                <span className="text-gray-600">{ep.idea.slice(0, 60)}...</span>
                <div className="mt-0.5 text-red-600">
                  Step: {ep.errorStep} &mdash; {ep.error}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
